"use client"

import { Pencil, Trash2, Shield, CheckCircle, XCircle, Clock, AlertCircle } from "lucide-react"
import type { Admin } from "../../../../lib/admin-data"

interface AdminTableRowProps {
  admin: Admin
  onEdit: (admin: Admin) => void
  onDelete: (id: string) => void
}

const statusConfig = {
  Active: { icon: CheckCircle, color: "text-green-600", bg: "bg-green-100" },
  Inactive: { icon: XCircle, color: "text-gray-600", bg: "bg-gray-100" },
  Suspended: { icon: AlertCircle, color: "text-red-600", bg: "bg-red-100" },
  "In Review": { icon: Clock, color: "text-amber-600", bg: "bg-amber-100" },
}

export function AdminTableRow({ admin, onEdit, onDelete }: AdminTableRowProps) {
  const StatusIcon = statusConfig[admin.status].icon

  return (
    <tr className="hover:bg-muted/50 transition-colors">
      <td className="p-5">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">
            {admin.name.split(" ")[0][0]}
            {admin.name.split(" ")[1]?.[0]}
          </div>
          <div>
            <p className="font-bold text-foreground">{admin.name}</p>
            <p className="text-xs text-muted-foreground">{admin.email}</p>
          </div>
        </div>
      </td>
      <td className="p-5">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-primary" />
          <span className="text-sm text-muted-foreground">{admin.role}</span>
        </div>
      </td>
      <td className="p-5 text-sm text-muted-foreground">{admin.department}</td>
      <td className="p-5">
        <div className="flex flex-wrap gap-1">
          {admin.permissions.slice(0, 2).map((permission, idx) => (
            <span
              key={idx}
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
            >
              {permission}
            </span>
          ))}
          {admin.permissions.length > 2 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              +{admin.permissions.length - 2} more
            </span>
          )}
        </div>
      </td>
      <td className="p-5">
        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${statusConfig[admin.status].bg}`}>
          <StatusIcon className={`w-3.5 h-3.5 ${statusConfig[admin.status].color}`} />
          <span className={`text-xs font-medium ${statusConfig[admin.status].color}`}>{admin.status}</span>
        </div>
      </td>
      <td className="p-5 text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => onEdit(admin)}
            className="p-2 text-primary hover:bg-teal-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Pencil className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(admin.id)}
            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  )
}
